import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  BriefcaseIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Download } from 'lucide-react';

const JobApplication = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data - Replace with actual API call
  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/application/all`);
      setApplications(response.data.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: ClockIcon },
      'under-review': { color: 'bg-blue-100 text-blue-700', icon: ClockIcon },
      shortlisted: { color: 'bg-green-100 text-green-700', icon: CheckCircleIcon },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircleIcon },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircleIcon }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status?.replace('-', ' ') || 'Pending'}
      </span>
    );
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.jobType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });


  const downloadFile = async (url, filename = "file.pdf") => {
    console.log("first", url);
  try {
    const res = await fetch(`${url}?fl_attachment`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed", err);
  }
};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
 <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 ">
    {/* Header */}
    <div className="mb-6 sm:mb-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
        Job Applications
      </h1>
      <p className="text-sm sm:text-base text-gray-600">
        Manage and review all candidate applications
      </p>
    </div>

    {/* Filters - Responsive Stack */}
    <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        {/* Search - Full width on mobile */}
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 lg:top-6 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        {/* Filter controls - Stack on mobile, row on desktop */}
        <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
          <div className="flex items-center gap-2 w-full xs:w-auto">
            <FunnelIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 xs:flex-none px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under-review">Under Review</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          <button
            onClick={fetchApplications}
            className="p-2 sm:p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors self-end xs:self-auto"
            title="Refresh"
          >
            <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    </div>

    {/* Applications Table - Card View on Mobile, Table on Desktop */}
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Mobile View (Card Layout) - Visible only on small screens */}
      <div className="block lg:hidden">
        {filteredApplications.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredApplications.map((app) => (
              <div key={app.id} className="p-4 hover:bg-gray-50 transition-colors">
                {/* Header with name and status */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{app.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <EnvelopeIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{app.email}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {getStatusBadge(app.status)}
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="text-sm text-gray-900 truncate">{app.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Position</p>
                    <p className="text-sm text-gray-900 truncate">{app?.jobId?.title}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Location</p>
                    <p className="text-sm text-gray-900 truncate">{app?.jobId?.locations?.join(", ")}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Applied Date</p>
                    <p className="text-sm text-gray-900 flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span>{new Date(app.createdAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                </div>

                {/* Action button */}
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => downloadFile(app.resume, "CandidateResume.pdf")}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download Resume
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State - Mobile */
          <div className="text-center py-8 sm:py-12 px-4">
            <UserIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
              No applications found
            </h3>
            <p className="text-sm text-gray-500">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>

      {/* Desktop View (Table) - Hidden on mobile */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Applicant
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Info
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Applied Date
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resume
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplications.map((app) => (
              <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <div className="text-sm font-medium text-gray-900">{app.name}</div>
                      <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[150px] sm:max-w-[200px]">
                        {app.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-gray-900">{app.phone}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[150px]">{app.email}</div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-gray-900">{app?.jobId?.title}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[150px]">
                    {app?.jobId?.locations?.join(", ")}
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm text-gray-900 flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                    <span className="whitespace-nowrap">{new Date(app.createdAt).toLocaleDateString()}</span>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(app.status)}
                </td>
                <td className="px-4 sm:px-6 py-4 text-right">
                  <button
                    onClick={() => downloadFile(app.resume, "CandidateResume.pdf")}
                    className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors inline-flex items-center gap-1"
                    title="Download Resume"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden xl:inline text-sm">Download</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State - Desktop (shown when table is hidden but no results) */}
      {filteredApplications.length === 0 && (
        <div className="hidden lg:block text-center py-12">
          <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>

    {/* Pagination - Responsive */}
    <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
        Showing <span className="font-medium">1</span> to{' '}
        <span className="font-medium">{Math.min(10, filteredApplications.length)}</span> of{' '}
        <span className="font-medium">{filteredApplications.length}</span> results
      </p>
      
      <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto justify-center">
        <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
          Previous
        </button>
        <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors">
          Next
        </button>
      </div>
    </div>
  </div>
</div>
  );
};

export default JobApplication;